/**
 * Copyright 2014-2019 MathWorks, Inc.
 * This utility module provides platform(Mac/Windows) related support.
 * The getPlatformSpecificString method can be used to provide the sanitized and platformized
 * version of a string.
 * For example:
 * input: "Control+s" => Windows: "Ctrl+S" & Mac: "⌘S"
 * input: "cntrl_shift_enter" => Windows: "Ctrl+Shift+Enter" & Mac: "⌘⇧Enter"
 */

define([
    "dojo/sniff"
], function (sniff) {
    // Keys in Windows shortcuts are separated with "+", whereas Mac keys are not.
    var SEPARATOR = sniff("mac") ? "" : "+";

    // NumPad keys are represented in a few ways depending on platform and if the key is a number.
    // Examples:
    // Windows: "NumPad-1", "NumPad-2", "NumPad *", "Numpad /"
    // Mac: "1", "2", "*", "/"
    var NUMPAD_PLATFORMIZED_NUMERIC_PREFIX = sniff("mac") ? "" : "NumPad-";
    var NUMPAD_PLATFORMIZED_NONNUMERIC_PREFIX = sniff("mac") ? "" : "NumPad ";

    // Internally, all NumPad keys are represented in the form "NUMPAD-1", "NUMPAD-*", etc.
    var NUMPAD_INTERNAL_PREFIX = "NUMPAD-";

    // Regex that matches if the the key (e.g. "NUMPAD-4") is for a number (as opposed to "/", etc.)
    var IS_NUMERIC_NUMPAD_KEY_REGEX = /NUMPAD-\d/;

    // Standard key ordering. This order is the same for all platforms, although some won't have meta.
    var SORT_ORDER = ["CTRL", "ALT", "SHIFT", "META"];

    // Map from generic key representations to an object with platform-specific representations.
    var stringMap = {
        CTRL:      {macString: "\u2303", otherPlatformString: "Ctrl"     },
        META:      {macString: "\u2318", otherPlatformString: "Ctrl"     },
        SHIFT:     {macString: "\u21E7", otherPlatformString: "Shift"    },
        ALT:       {macString: "\u2325", otherPlatformString: "Alt"      },
        ENTER:     {macString: "\u23CE", otherPlatformString: "Enter"    },
        DELETE:    {macString: "\u2326", otherPlatformString: "Delete"   },
        DEL:       {macString: "\u2326", otherPlatformString: "Del"      },
        SPACEBAR:  {macString: "\u2423", otherPlatformString: "Spacebar" },
        SPACE:     {macString: "\u2423", otherPlatformString: "Space"    },
        UP:        {macString: "\u2191", otherPlatformString: "Up"       },
        DOWN:      {macString: "\u2193", otherPlatformString: "Down"     },
        LEFT:      {macString: "\u2190", otherPlatformString: "Left"     },
        RIGHT:     {macString: "\u2192", otherPlatformString: "Right"    },
        HOME:      {macString: "\u2196", otherPlatformString: "Home"     },
        END:       {macString: "\u2198", otherPlatformString: "End"      },
        PAGEUP:    {macString: "\u21DE", otherPlatformString: "Page Up"  },
        PAGEDOWN:  {macString: "\u21DF", otherPlatformString: "Page Down"},
        TAB:       {macString: "\u21E5", otherPlatformString: "Tab"      },
        ESCAPE:    {macString: "\u238B", otherPlatformString: "Esc"      },
        BACKSPACE: {macString: "\u232B", otherPlatformString: "Backspace"},
        CLEAR:     {macString: "\u2327", otherPlatformString: "Clear"    }, // There is no Clear key on Windows
        PAUSE:     {macString: "Pause",  otherPlatformString: "Pause"    }, // There is no Pause key on Mac
        INSERT:    {macString: "Insert", otherPlatformString: "Insert"   } // There is no Insert key on Mac
    };

    /**
     * Gets the platform-specific representation of a NumPad key.
     * @param key A numpad key in the form "NUMPAD-9", "NUMPAD-+", etc.
     */
    function platformizeNumpadKey(key) {
        var numPadPrefix = key.match(IS_NUMERIC_NUMPAD_KEY_REGEX) ?
            NUMPAD_PLATFORMIZED_NUMERIC_PREFIX : NUMPAD_PLATFORMIZED_NONNUMERIC_PREFIX;
        return key.replace(NUMPAD_INTERNAL_PREFIX, numPadPrefix);
    }

    /**
     * Reorders an array of modifiers to the standard ordering. The sort is done in place.
     * @param {array} keys Array of any of the following modifiers: "CTRL", "ALT", "SHIFT", "META"
     */
    function reorderModifiers(keys) {
        keys.sort(function (a, b) {
            return SORT_ORDER.indexOf(a) - SORT_ORDER.indexOf(b);
        });
    }

    return {
        /**
         * Gets the platform-specific representation of a keyboard shortcut.
         *
         * The following special keys are handled:
         * ctrl, cntrl, control, meta, shift, alt, enter, delete, del, spacebar, backspace, space,
         * up, pageup, pagedown, down, home, end, left, right, tab, escape, clear*, pause^, insert^
         *     * clear is only on Mac
         *     ^ pause and insert are not supported on Mac.
         *
         * "ctrl"/"cntrl"/"control" will automatically be changed to ⌘ on Mac
         * "meta" will automatically be changed to Ctrl on other platforms.
         *
         * NumPad keys are supported. They should be specified using "numpad-" as a prefix.
         * For example, "ctrl_numpad-9" or "ctrl_numpad-+"
         *
         * @param str The shortcut to platformize. Keys should be separated with "+" or "_". examples:
         * "Control+x", "Ctrl_x", "ctrl_alt_x"
         * @param options Optional args object
         * @param options.dontConvertCtrlToCmd By default, Ctrl will be converted to meta on Mac.
         * Set this flag to allow Ctrl on Mac.
         * @param options.sortModifiers Set this flag to reorder modifiers per MATLAB platform standards.
         * This should typically be set to true. For backward compatibility, the default
         * does not sort.
         * @return {String} The platformized shortcut
         */
        getPlatformSpecificString: function (str, options) {
            options = options || {};

            // Remove all spaces. For example: control + f => control+f
            str = str.replace(/ /g, "");

            // Replace underscores with '+'. For example: control_f => control+f
            str = str.replace(/_/g, "+");

            // Make everything uppercase. For example: control+f => CONTROL+F
            str = str.toUpperCase();

            // Handle some alternate representations of "ctrl"
            str = str.replace(/CNTRL|CONTROL/g, "CTRL");

            // By default, replace CTRL with META
            if (sniff("mac") && !options.dontConvertCtrlToCmd) {
                str = str.replace(/CTRL/g, "META");
            }
            // For a trailing "+", we support both "Ctrl+" and "Ctrl++".
            // Handle missing "+" for the case "Ctrl+". For example: Ctrl+ => Ctrl++
            // This will not add an extra "+" to NUMPAD-+.
            if (/[^+-]\+$/.test(str)) {
                str += "+";
            }

            // Split the string into its components. E.g.
            // META+SHIFT+A => ["META", "SHIFT", "A"]
            // The negative lookahead ensures that we do not lose a trailing plus. For example:
            // META+SHIFT++ => ["META", "SHIFT", "+"]
            var keys = str.split(/\+(?!$)/);

            // Platformize the NumPad prefix. For example:
            //    Windows: ["CTRL", "NUMPAD-9"] => ["CTRL", "NumPad-9"]
            //    Windows: ["CTRL", "NUMPAD--"] => ["CTRL", "NumPad -"]
            //    Mac:     ["CTRL", "NUMPAD-9"] => ["CTRL", "9"]
            var lastKey = keys.pop();
            if (lastKey.indexOf(NUMPAD_INTERNAL_PREFIX) === 0) {
                lastKey = platformizeNumpadKey(lastKey);
            }

            // Order the modifiers per system guidelines. E.g. On Mac, Option should be before Command.
            if (options.sortModifiers) {
                reorderModifiers(keys);
            }

            keys.push(lastKey);

            // Convert all special keys to their corresponding platform equivalents.
            // For example: CONTROL+F => Ctrl+F or ⌘+F
            var platformizedKeys = keys.map(function (key){
                var mappedKeys = stringMap[key];
                if (!mappedKeys) {
                    return key;
                }

                return sniff("mac") ? mappedKeys.macString : mappedKeys.otherPlatformString;
            });

            return platformizedKeys.join(SEPARATOR);
        }
    };
});